import { renderAdmin } from '../admin.js';
import { requireKey, saveKey } from '../api.js';
import { genDesignPreview, renderDesign } from '../build/design.js';
import { $, hide, show, showErr } from '../dom.js';
import { getExOptionConfig } from '../logic/existing.ts';
import { ST } from '../state.js';
import { assembleEnrichment, renderBrief, renderFollowups, renderSummary } from './brief.js';
import {
  brAddColor,
  brAddFact,
  brAddImages,
  brAddTag,
  brDelImage,
  brEditColor,
  brEditConv,
  brEditField,
  briefReviewBack,
  brRemoveFact,
  brRemoveTag,
  brSaveConv,
  proceedFromBriefReview,
  regenerateBrief,
  downloadBrief,
  renderBriefReview,
} from './brief-review.js';
import { chatFrameId, sendChatMessage, undoChat } from './chat.js';
import {
  updateExProceedBtn,
  analyseExisting,
  pickPath,
  proceedFromExisting,
  setExOpt,
  startOver,
} from './existing.js';
import {
  doPublish,
  downloadZip,
  publish,
  renderBuild,
  renderBuildTabContent,
  renderBuildTabs,
} from './sections.js';
import {
  runQAEnrichment,
  proceedFromDocview,
  proceedFromUpload,
  renderDocView,
  renderUploadScreen,
} from './uploads.js';

// ── NAVIGATION & G OBJECT ─────────────────────────────────────────
// Central navigation controller and global action dispatcher.
// During Vue migration, G.go() becomes vue-router navigation
// and individual methods become Pinia actions or composable calls.

/**
 * Global action dispatcher — exposes screen navigation and all
 * user-facing actions to onclick handlers throughout the app.
 */
const G = {
  toggleSb(which) {
    const panels = {
      sects: 'sect-sb-panel',
      prev: 'edit-sb-panel',
      design: 'design-sidebar',
      build: 'build-sb-panel',
    };
    const backdrops = {
      sects: 'sects-backdrop',
      prev: 'prev-backdrop',
      design: 'design-backdrop',
      build: 'build-backdrop',
    };
    const panel = document.getElementById(panels[which]);
    const backdrop = document.getElementById(backdrops[which]);
    if (!panel) {
      return;
    }
    const isOpen = !panel.classList.contains('collapsed');
    if (isOpen) {
      panel.classList.add('collapsed');
      backdrop?.classList.remove('visible');
    } else {
      panel.classList.remove('collapsed');
      backdrop?.classList.add('visible');
    }
  },
  go(screen) {
    // Clear any active streaming intervals to prevent leaked writes
    if (ST.activeStreamTicks?.length) {
      ST.activeStreamTicks.forEach(t => clearInterval(t));
      ST.activeStreamTicks = [];
    }
    // Clear pending image analysis debounce
    if (ST.imgAnalysisTimer) {
      clearTimeout(ST.imgAnalysisTimer);
      ST.imgAnalysisTimer = null;
    }
    hide(`sc-${ST.screen}`);
    ST.prev = ST.screen;
    ST.screen = screen;
    show(`sc-${screen}`);
    const map = {
      start: renderStart,
      brief: renderBrief,
      existing: renderExisting,
      'ex-detail': renderExDetail,
      'brief-review': renderBriefReview,
      upload: renderUpload,
      docview: renderDocView,
      followups: renderFollowups,
      summary: renderSummary,
      concept: () => G.go('design'),
      design: renderDesign,
      build: renderBuild,
      admin: renderAdmin,
    };

    function renderStart() {
      /* static HTML, nothing to render */
    }

    function renderExisting() {
      const inp = $('ex-url-inp');
      if (inp && ST.existingUrl) {
        inp.value = ST.existingUrl;
      }
    }

    function renderExDetail() {
      const opt = ST.existingOpt || 'keep';
      ['keep', 'refresh', 'rebrand'].forEach(o => {
        const el = $(`exd-fields-${o}`);
        if (el) {
          el.style.display = o === opt ? 'flex' : 'none';
        }
        const card = $(`exd-opt-${o}`);
        if (card) {
          card.classList.toggle('selected', o === opt);
        }
      });
      const cfg = getExOptionConfig(opt);
      const chipWrap = $('exd-chip-wrap');
      if (chipWrap) {
        chipWrap.innerHTML = `<div class="exd-chip ${cfg.cls}">${cfg.label}</div>`;
      }
      const h1 = $('exd-h1');
      if (h1) {
        h1.textContent = cfg.h1;
      }
      const lead = $('exd-lead');
      if (lead) {
        lead.textContent = cfg.lead;
      }
      updateExProceedBtn();
    }

    function renderUpload() {
      renderUploadScreen();
    }

    try {
      map[screen]?.();
    } catch (e) {
      console.error('Render error on', screen, e);
      showErr(`Render error: ${e.message}`);
    }
  },
  downloadZip,
  publish,
  doPublish,
  sendChat() {
    const ta = $('chat-ta');
    const text = ta?.value?.trim() || '';
    if (!text && !ST.chatImage) {
      return;
    }
    if (ta) ta.value = '';
    requireKey(() => {
      void sendChatMessage(text, { frameId: chatFrameId });
    });
  },
  undoChat,
  switchTab(tab) {
    ST.chatTab = tab;
    renderBuildTabs();
    renderBuildTabContent();
  },
  saveKey,
  pickPath,
  setExOpt,
  startOver,
  genDesignPreview,
  analyseExisting,
  proceedFromExisting,
  proceedFromUpload,
  proceedFromDocview,
  proceedFromBriefReview: proceedFromBriefReview,
  briefReviewBack: briefReviewBack,
  regenerateBrief: regenerateBrief,
  brEdit: brEditField,
  brRemoveTag: brRemoveTag,
  brAddTag: brAddTag,
  brEditColor: brEditColor,
  brAddColor: brAddColor,
  brDelImage: brDelImage,
  brAddImages: brAddImages,
  brAddFact: brAddFact,
  brRemoveFact: brRemoveFact,
  brEditConv: brEditConv,
  brSaveConv: brSaveConv,
  onRebrandInput: updateExProceedBtn,
  downloadBrief,
  skipFollowups() {
    ST.followUps.forEach(f => {
      f.skipped = true;
    });
    G.go('summary');
  },
  startConceptFromSummary() {
    assembleEnrichment();
    requireKey(() => {
      runQAEnrichment();
    });
  },
};
window.G = G; // Keep global for backward compat during migration

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export { G };
